import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { DirectDebitPayment, LegacyDirectDebitFrame } from '@PetSureAustralia/petsure-payment/react-wrapper';
import {
  Icon,
  InputCheckbox,
  Modal,
  StackedIcons,
  StyledRadios
} from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { RichTextParser } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { LegacyUpdatePremiumPayment } from '../legacyPayment/updatePremiumPayment/legacyUpdatePremiumPayment';
import { FullPetDetailsPropTypes } from '../../../propTypes/petDetails';
import { usePremiumPaymentPopUp } from './usePremiumPaymentPopUp';
import './premiumPaymentPopup.scss';
import { FORM_KEYS } from '../../../constants/formKeys';
import { PAYMENT_METHOD_OPTION, PETSURE_UNDERWRITER } from '../../../constants/constants';
import { PremiumPaymentFooter } from './premiumPaymentFooter';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { TooltipTrigger } from '../../tooltipTrigger/tooltipTrigger';
// eslint-disable-next-line import/no-unresolved
import '@PetSureAustralia/petsure-payment/react-wrapper/index.esm.css';

export const PremiumPaymentPopup = ({ onCancel, activePet, petDetails, setShowSuccessMessage }) => {
  const {
    showFailedUpdateError,
    showSelectAllCheckbox,
    returnUrlState,
    paymentMethod,
    partner,
    fieldFormContext,
    formUrl,
    supplierBusinessCode,
    partnerPrefix,
    ddRef,
    soleAuthorityTooltip,
    handleClose,
    handlePaymentMethodChange,
    handleUpdateDirectDebit,
    handlePaymentSuccess
  } = usePremiumPaymentPopUp(activePet, petDetails, onCancel, setShowSuccessMessage);

  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isLegacyPaymentEnabled = cspConfiguration?.IsLegacyPaymentEnabled || false;

  const isCard = paymentMethod === PAYMENT_METHOD_OPTION.CARD;
  const isDirectDebit = paymentMethod === PAYMENT_METHOD_OPTION.DIRECT_DEBIT;
  const isDirectDebitPetSure = isDirectDebit && activePet.Underwriter === PETSURE_UNDERWRITER;
  const showSelectAll = showSelectAllCheckbox && petDetails.length > 1;
  const isPartnerUsingLegacyPayment = partner === 'trupanion';

  return (
    <Modal
      size="small"
      open
      onClose={handleClose}
      header={<h2 className="modal-title">Update my premium payment details</h2>}
      customCssClass="updatePremiumPaymentDialog"
      isContentScrollable
      footer={
        !isLegacyPaymentEnabled || !isPartnerUsingLegacyPayment ? (
          <PremiumPaymentFooter
            paymentMethod={paymentMethod}
            handleClose={handleClose}
            handleUpdateDirectDebit={handleUpdateDirectDebit}
            cardPaymentProps={{
              formUrl,
              activePet,
              returnUrlState,
              supplierBusinessCode,
              partnerPrefix
            }}
          />
        ) : null
      }
    >
      {isLegacyPaymentEnabled && isPartnerUsingLegacyPayment ? (
        <LegacyUpdatePremiumPayment
          activePet={activePet}
          petDetails={petDetails}
          onCancel={onCancel}
          setShowSuccessMessage={setShowSuccessMessage}
          isPartnerUsingLegacyPayment={isPartnerUsingLegacyPayment}
        />
      ) : (
        <>
          <h3 className="title">Payment Method</h3>
          <StyledRadios
            name="premiumPayment"
            onChange={handlePaymentMethodChange}
            hideGroupLabel
            groupName="premiumPayment"
            radioInputs={[
              {
                id: 'digital',
                label: 'Credit/Debit card',
                value: PAYMENT_METHOD_OPTION.CARD,
                labelCssClass: { checked: 'button primary', unchecked: 'button primary outline' },
                icon: {
                  checked: (
                    <StackedIcons
                      customCssClass="offsetTick"
                      icons={[
                        <Icon
                          key="icon1"
                          icon="radio-unchecked"
                        />,
                        <Icon
                          key="icon2"
                          icon="tick"
                          customCssClass="reveal animate-slow animate-once"
                        />
                      ]}
                    />
                  ),
                  unchecked: <Icon icon="radio-unchecked" />
                }
              },
              {
                id: 'post',
                label: 'Direct debit',
                value: PAYMENT_METHOD_OPTION.DIRECT_DEBIT,
                labelCssClass: { checked: 'button primary', unchecked: 'button primary outline' },
                icon: {
                  checked: (
                    <StackedIcons
                      customCssClass="offsetTick"
                      icons={[
                        <Icon
                          key="icon1"
                          icon="radio-unchecked"
                        />,
                        <Icon
                          key="icon2"
                          icon="tick"
                          customCssClass="reveal animate-slow animate-once"
                        />
                      ]}
                    />
                  ),
                  unchecked: <Icon icon="radio-unchecked" />
                }
              }
            ]}
            value={paymentMethod}
            fieldFormContext={fieldFormContext}
          />
        </>
      )}

      {isDirectDebit && (!isLegacyPaymentEnabled || !isPartnerUsingLegacyPayment) && (
        <div className="petSureForm verticalStacked premiumPaymentPopup">
          {isDirectDebitPetSure ? (
            <DirectDebitPayment formContext={fieldFormContext} />
          ) : (
            <div className="directDebitContainer">
              <LegacyDirectDebitFrame
                ref={ddRef}
                publishableApiKey={
                  process.env.REACT_APP_ENV !== 'prod'
                    ? process.env.WESTPAC_PUBLISHABLE_API_KEY_DEV
                    : process.env.WESTPAC_PUBLISHABLE_API_KEY_PROD
                }
                supplierBusinessCode={supplierBusinessCode}
                westpacScriptUrl={
                  process.env.REACT_APP_ENV !== 'prod'
                    ? process.env.WESTPAC_SCRIPT_URL_DEV
                    : process.env.WESTPAC_SCRIPT_URL_PROD
                }
                onPaymentSuccess={handlePaymentSuccess}
              />
            </div>
          )}
        </div>
      )}

      {isCard && (!isLegacyPaymentEnabled || !isPartnerUsingLegacyPayment) && (
        <div className="pleaseNoteMessage">
          <span>
            <b>Please note:</b> To safely complete updating your payment details, you will be redirected to our secure
            payment provider.
          </span>
        </div>
      )}

      {isDirectDebitPetSure && (!isLegacyPaymentEnabled || !isPartnerUsingLegacyPayment) && (
        <span>
          By clicking Update, I authorise PetSure (Australia) Pty Ltd (the Initiator) (User ID 647811) to debit my
          account with the amounts outlined by the Initiator by direct debit. I confirm, I have{' '}
          <strong>sole authority</strong>{' '}
          <TooltipTrigger
            id="sole-authority-tooltip"
            trigger={<Icon icon="information" />}
            content={<>{RichTextParser(soleAuthorityTooltip)}</>}
          />
          &nbsp;as the account holder over the bank account number above. I have read and accepted the&nbsp;
          <a
            href={`https://petcdnstore.blob.core.windows.net/common-assets/service_agreement/${
              partnerPrefix ?? 'WW'
            }/Direct_Debit_Service_Agreement_${partnerPrefix ?? 'WW'}.pdf`}
            target="_blank"
            rel="noreferrer"
          >
            Direct Debit Request Service Agreement
          </a>
          .
        </span>
      )}

      {showSelectAll && (!isLegacyPaymentEnabled || !isPartnerUsingLegacyPayment) && (
        <fieldset className="applyToAllCheckboxPanel">
          <InputCheckbox
            fieldFormContext={fieldFormContext}
            name={FORM_KEYS.APPLY_TO_ALL_PETS}
            label="Apply to all my pets"
            customCssClass="applyToAllCheckbox"
          />
        </fieldset>
      )}

      {showFailedUpdateError && (
        <div className="error-message-container">
          <span className="error-message">
            Sorry, we couldn&apos;t update your details at this time. Please try again in a few minutes or
            <a
              href="/contact-us"
              rel="noreferrer"
            >
              &nbsp;contact us&nbsp;
            </a>
            for assistance.
          </span>
        </div>
      )}
    </Modal>
  );
};

PremiumPaymentPopup.propTypes = {
  activePet: FullPetDetailsPropTypes,
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  onCancel: PropTypes.func,
  setShowSuccessMessage: PropTypes.func
};
